import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mockApi } from '@/core/services/api';
import type { Camera, CameraSettings } from '@smolt/shared/types';

/**
 * Get camera list (ID and name only) for dashboard
 * Lightweight query for listing cameras
 */
export const useCameraList = () => {
  return useQuery({
    queryKey: ['cameraList'],
    queryFn: mockApi.getCameraList,
    refetchInterval: 5000, // Slow polling - IDs rarely change
  });
};

export const useCameraById = (id: string) => {
  return useQuery({
    queryKey: ['camera', id],
    queryFn: () => mockApi.getCameraById(id),
    enabled: !!id,
    refetchInterval: (query) => {
      // Check if this camera is counting
      const isCounting = query.state.data?.sessionState === 'counting';
      // Poll every 200ms when counting (5 times per second), otherwise every 5 seconds
      return isCounting ? 200 : 5000;
    },
  });
};

export const useUpdateCameraSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, settings }: { id: string; settings: CameraSettings }) =>
      mockApi.updateCameraSettings(id, settings),
    onSuccess: (updatedCamera, { id }) => {
      // Update individual camera cache immediately
      queryClient.setQueryData(['camera', id], { ...updatedCamera });
    },
  });
};

export const useStartCounting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cameraAction', 'start'],
    mutationFn: (id: string) => mockApi.startCounting(id),
    onMutate: async (id: string) => {
      // Cancel outgoing refetches for this specific camera
      await queryClient.cancelQueries({ queryKey: ['camera', id] });

      // Snapshot previous value
      const previousCamera = queryClient.getQueryData(['camera', id]);

      // Optimistically update individual camera query
      queryClient.setQueryData(['camera', id], (old: Camera | undefined) => {
        if (!old) return old;
        return { ...old, sessionState: 'starting' as const };
      });

      return { previousCamera, id };
    },
    onSuccess: (updatedCamera, id) => {
      // Immediately apply API response to individual camera cache
      queryClient.setQueryData(['camera', id], updatedCamera);
    },
    onError: (_err, _id, context) => {
      // Rollback individual camera query
      if (context?.previousCamera) {
        queryClient.setQueryData(['camera', context.id], context.previousCamera);
      }
    },
  });
};

export const useStopCounting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cameraAction', 'stop'],
    mutationFn: (id: string) => mockApi.stopCounting(id),
    onMutate: async (id: string) => {
      // Cancel outgoing refetches for this specific camera
      await queryClient.cancelQueries({ queryKey: ['camera', id] });

      // Snapshot previous value
      const previousCamera = queryClient.getQueryData(['camera', id]);

      // Optimistically update individual camera query
      queryClient.setQueryData(['camera', id], (old: Camera | undefined) => {
        if (!old) return old;
        return { ...old, sessionState: 'stopping' as const };
      });

      return { previousCamera, id };
    },
    onSuccess: (updatedCamera, id) => {
      // Immediately apply API response to individual camera cache
      queryClient.setQueryData(['camera', id], updatedCamera);

      // Invalidate history query to fetch updated records with tank info
      queryClient.invalidateQueries({ queryKey: ['history', id] });
    },
    onError: (_err, _id, context) => {
      // Rollback individual camera query
      if (context?.previousCamera) {
        queryClient.setQueryData(['camera', context.id], context.previousCamera);
      }
    },
  });
};

export const useStopAllCounting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cameraAction', 'stopAll'],
    mutationFn: () => mockApi.stopAllCounting(),
    onMutate: async () => {
      // Get camera IDs from cameraList query
      const cameraList = queryClient.getQueryData(['cameraList']) as { id: string }[] | undefined;
      if (!cameraList) return { previousCameras: new Map<string, Camera>() };

      const previousCameras = new Map<string, Camera>();

      // Iterate over each camera and update its individual query
      for (const { id } of cameraList) {
        await queryClient.cancelQueries({ queryKey: ['camera', id] });
        const prev = queryClient.getQueryData(['camera', id]) as Camera | undefined;
        if (prev) {
          previousCameras.set(id, prev);
          // Optimistically update counting/starting cameras to stopping state
          if (prev.sessionState === 'counting' || prev.sessionState === 'starting') {
            queryClient.setQueryData(['camera', id], {
              ...prev,
              sessionState: 'stopping' as const,
            });
          }
        }
      }

      return { previousCameras };
    },
    onSuccess: (updatedCameras) => {
      // Apply API response to individual camera caches
      updatedCameras.forEach((camera) => {
        queryClient.setQueryData(['camera', camera.id], camera);
        // Invalidate history query to fetch updated records with tank info
        queryClient.invalidateQueries({ queryKey: ['history', camera.id] });
      });
    },
    onError: (_err, _variables, context) => {
      // Rollback individual camera queries
      if (context?.previousCameras) {
        context.previousCameras.forEach((camera, id) => {
          queryClient.setQueryData(['camera', id], camera);
        });
      }
    },
  });
};

export const useStartAllCounting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cameraAction', 'startAll'],
    mutationFn: () => mockApi.startAllCounting(),
    onMutate: async () => {
      // Get camera IDs from cameraList query
      const cameraList = queryClient.getQueryData(['cameraList']) as { id: string }[] | undefined;
      if (!cameraList) return { previousCameras: new Map<string, Camera>() };

      const previousCameras = new Map<string, Camera>();

      // Iterate over each camera and update its individual query
      for (const { id } of cameraList) {
        await queryClient.cancelQueries({ queryKey: ['camera', id] });
        const prev = queryClient.getQueryData(['camera', id]) as Camera | undefined;
        if (prev) {
          previousCameras.set(id, prev);
          // Optimistically update ready cameras to starting state
          if (prev.cameraState === 'playing' && prev.sessionState === 'ready') {
            queryClient.setQueryData(['camera', id], {
              ...prev,
              sessionState: 'starting' as const,
            });
          }
        }
      }

      return { previousCameras };
    },
    onSuccess: (updatedCameras) => {
      // Apply API response to individual camera caches
      updatedCameras.forEach((camera) => {
        queryClient.setQueryData(['camera', camera.id], camera);
      });
    },
    onError: (_err, _variables, context) => {
      // Rollback individual camera queries
      if (context?.previousCameras) {
        context.previousCameras.forEach((camera, id) => {
          queryClient.setQueryData(['camera', id], camera);
        });
      }
    },
  });
};
